import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useHesapAuth } from '../../store/hesapStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Edit2, Trash2, Loader2, Building2, ChevronRight, ChevronLeft,
  Home, User, Phone, CreditCard, Check, X, DollarSign, Calendar,
  Wrench, RefreshCw, FileText, Download, CheckCircle, Camera, Upload,
  Folder, Image as ImageIcon, Eye, FileX
} from 'lucide-react';

const API = `${RUNTIME_BACKEND_URL}/api/hesap`;

export default function HesapApartmentPage() {
  const { token } = useHesapAuth();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [apartments, setApartments] = useState([]);
  const [selectedApt, setSelectedApt] = useState(null);
  const [aptDetail, setAptDetail] = useState(null);
  const [duesSummary, setDuesSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // Dialogs
  const [showAptDialog, setShowAptDialog] = useState(false);
  const [showUnitDialog, setShowUnitDialog] = useState(false);
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [showDuesUpdateDialog, setShowDuesUpdateDialog] = useState(false);
  const [showTahsilatDialog, setShowTahsilatDialog] = useState(false);
  const [showEvrakSection, setShowEvrakSection] = useState(false);
  const [editingApt, setEditingApt] = useState(null);
  const [editingUnit, setEditingUnit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [duesFilter, setDuesFilter] = useState('all'); // all, borcu_olan, borcu_yok
  const [payType, setPayType] = useState('aidat'); // aidat, ortak_gider
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [unitDebts, setUnitDebts] = useState([]);
  const [loadingUnit, setLoadingUnit] = useState(false);

  // Tahsilat (collection) modal state
  const [tahsilatForm, setTahsilatForm] = useState({
    unit_id: '', tutar: 0, payment_method: 'nakit', note: ''
  });

  // Daire detay modal
  const [showUnitDetailDialog, setShowUnitDetailDialog] = useState(false);
  const [unitDetail, setUnitDetail] = useState(null);
  const [loadingUnitDetail, setLoadingUnitDetail] = useState(false);
  const [showExtraDebtForm, setShowExtraDebtForm] = useState(false);
  const [extraDebtForm, setExtraDebtForm] = useState({ description: '', amount: 0, category: 'diger' });

  const CATEGORY_LABELS = {
    cati_bakim: 'Çatı Bakım', asansor: 'Asansör', temizlik: 'Temizlik',
    boya_badana: 'Boya/Badana', tessat: 'Tesisat', bahce: 'Bahçe',
    guvenlik: 'Güvenlik', diger: 'Diğer'
  };
  const getCategoryLabel = (cat) => CATEGORY_LABELS[cat] || cat || '';

  const [aptForm, setAptForm] = useState({ name: '', address: '', total_units: 0, monthly_dues: 0, note: '' });
  const [unitForm, setUnitForm] = useState({
    unit_no: '', floor: '', area_m2: 0, unit_type: 'malik',
    resident_name: '', resident_phone: '', resident_phone2: '', move_in_date: '', note: ''
  });
  const [payForm, setPayForm] = useState({ unit_id: '', unit_no: '', resident_name: '', amount: 0, month: '', payment_method: 'nakit', note: '' });
  const [expenseForm, setExpenseForm] = useState({ description: '', amount: '', category: '', month: '' });
  const [duesForm, setDuesForm] = useState({ new_amount: '', period: 'aylik', start_month: '' });

  useEffect(() => { loadApartments(); }, []);

  const loadApartments = async () => {
    try {
      const res = await fetch(`${API}/apartments`, { headers });
      if (res.ok) setApartments(await res.json());
    } finally { setLoading(false); }
  };

  const loadAptDetail = useCallback(async (aptId) => {
    const res = await fetch(`${API}/apartments/${aptId}`, { headers });
    if (res.ok) {
      const data = await res.json();
      setAptDetail(data);
    }
  }, [token]);

  const loadDuesSummary = useCallback(async (aptId, month) => {
    const res = await fetch(`${API}/apartments/${aptId}/dues-summary?month=${month}`, { headers });
    if (res.ok) setDuesSummary(await res.json());
  }, [token]);

  useEffect(() => {
    if (selectedApt) {
      loadAptDetail(selectedApt.id);
      loadDuesSummary(selectedApt.id, selectedMonth);
    }
  }, [selectedApt, selectedMonth, loadAptDetail, loadDuesSummary]);

  // Apartment CRUD
  const openAptDialog = (apt = null) => {
    setEditingApt(apt);
    setAptForm(apt ? { name: apt.name, address: apt.address || '', total_units: apt.total_units || 0, monthly_dues: apt.monthly_dues || 0, note: apt.note || '' }
      : { name: '', address: '', total_units: 0, monthly_dues: 0, note: '' });
    setShowAptDialog(true);
  };

  const saveApartment = async (e) => {
    e.preventDefault();
    if (!aptForm.name.trim()) { toast.error('Apartman adı gerekli'); return; }
    setSaving(true);
    try {
      const url = editingApt ? `${API}/apartments/${editingApt.id}` : `${API}/apartments`;
      const method = editingApt ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(aptForm) });
      if (!res.ok) throw new Error('Hata');
      const saved = await res.json();
      toast.success(editingApt ? 'Güncellendi' : 'Apartman eklendi');
      setShowAptDialog(false);
      loadApartments();
      if (editingApt) { setSelectedApt(saved); loadAptDetail(saved.id); }
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const deleteApartment = async (apt) => {
    if (!window.confirm(`"${apt.name}" silinsin mi? Tüm daire ve aidat verileri de silinecek!`)) return;
    await fetch(`${API}/apartments/${apt.id}`, { method: 'DELETE', headers });
    toast.success('Apartman silindi');
    setSelectedApt(null);
    setAptDetail(null);
    loadApartments();
  };

  // Unit CRUD
  const openUnitDialog = (unit = null) => {
    setEditingUnit(unit);
    setUnitForm(unit ? {
      unit_no: unit.unit_no, floor: unit.floor || '', area_m2: unit.area_m2 || 0,
      unit_type: unit.unit_type || 'malik', resident_name: unit.resident_name || '',
      resident_phone: unit.resident_phone || '', resident_phone2: unit.resident_phone2 || '',
      move_in_date: unit.move_in_date || '', note: unit.note || ''
    } : { unit_no: '', floor: '', area_m2: 0, unit_type: 'malik', resident_name: '', resident_phone: '', resident_phone2: '', move_in_date: '', note: '' });
    setShowUnitDialog(true);
  };

  const saveUnit = async (e) => {
    e.preventDefault();
    if (!unitForm.unit_no.trim()) { toast.error('Daire no gerekli'); return; }
    setSaving(true);
    try {
      const url = editingUnit ? `${API}/units/${editingUnit.id}` : `${API}/units`;
      const method = editingUnit ? 'PUT' : 'POST';
      const body = editingUnit ? unitForm : { ...unitForm, apartment_id: selectedApt.id };
      const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Hata');
      toast.success(editingUnit ? 'Daire güncellendi' : 'Daire eklendi');
      setShowUnitDialog(false);
      loadAptDetail(selectedApt.id);
      loadDuesSummary(selectedApt.id, selectedMonth);
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const deleteUnit = async (unit) => {
    if (!window.confirm(`Daire ${unit.unit_no} silinsin mi?`)) return;
    await fetch(`${API}/units/${unit.id}`, { method: 'DELETE', headers });
    toast.success('Daire silindi');
    loadAptDetail(selectedApt.id);
    loadDuesSummary(selectedApt.id, selectedMonth);
  };

  // Dues Payment
  const loadUnitDebts = async (unitId) => {
    setLoadingUnit(true);
    try {
      const res = await fetch(`${API}/apartments/${selectedApt.id}/unit-debts/${unitId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setUnitDebts(data);
      }
    } catch { toast.error('Borç detayı yüklenemedi'); }
    finally { setLoadingUnit(false); }
  };

  const openUnitDetail = (unit) => {
    setSelectedUnit(unit);
    loadUnitDebts(unit.unit_id);
  };

  const downloadUnitPdf = async () => {
    if (!selectedUnit) return;
    try {
      const res = await fetch(`${API}/apartments/${selectedApt.id}/unit-pdf/${selectedUnit.unit_id}?month=${selectedMonth}`, { headers });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedApt.name}_Daire${selectedUnit.unit_no}_${selectedUnit.resident_name || ''}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF indirildi');
    } catch { toast.error('PDF oluşturulamadı'); }
  };

  const openPayDialog = (unit, type = 'aidat') => {
    const amount = type === 'ortak_gider' ? (unit.ortak_gider_borc || 0) : (selectedApt?.monthly_dues || duesSummary?.monthly_dues || 0);
    setPayType(type);
    setPayForm({
      unit_id: unit.unit_id || unit.id,
      unit_no: unit.unit_no,
      resident_name: unit.resident_name || '',
      amount: amount,
      month: selectedMonth,
      payment_method: 'nakit',
      note: ''
    });
    setShowPayDialog(true);
  };

  const saveDuesPayment = async (e) => {
    e.preventDefault();
    if (!payForm.amount) { toast.error('Tutar gerekli'); return; }
    setSaving(true);
    try {
      if (payType === 'ortak_gider') {
        const res = await fetch(`${API}/dues/pay-shared-expense`, {
          method: 'POST', headers,
          body: JSON.stringify({
            unit_id: payForm.unit_id,
            apartment_id: selectedApt.id,
            month: payForm.month,
            payment_method: payForm.payment_method
          })
        });
        if (!res.ok) throw new Error('Hata');
        toast.success('Ortak gider ödemesi kaydedildi');
      } else {
        const res = await fetch(`${API}/dues`, {
          method: 'POST', headers,
          body: JSON.stringify({ ...payForm, apartment_id: selectedApt.id })
        });
        if (!res.ok) throw new Error('Hata');
        toast.success('Aidat ödemesi kaydedildi');
      }
      setShowPayDialog(false);
      loadDuesSummary(selectedApt.id, selectedMonth);
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  // Shared Expense
  const openExpenseDialog = () => {
    setExpenseForm({ description: '', amount: '', category: '', month: selectedMonth });
    setShowExpenseDialog(true);
  };

  const saveExpense = async (e) => {
    e.preventDefault();
    if (!expenseForm.description.trim()) { toast.error('Açıklama gerekli'); return; }
    if (!expenseForm.amount || parseFloat(expenseForm.amount) <= 0) { toast.error('Tutar gerekli'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/apartments/${selectedApt.id}/shared-expense`, {
        method: 'POST', headers,
        body: JSON.stringify({
          apartment_id: selectedApt.id,
          description: expenseForm.description,
          amount: parseFloat(expenseForm.amount),
          month: expenseForm.month || selectedMonth,
          category: expenseForm.category
        })
      });
      if (!res.ok) throw new Error('Hata');
      const result = await res.json();
      toast.success(result.message);
      setShowExpenseDialog(false);
      loadDuesSummary(selectedApt.id, selectedMonth);
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const deleteExpense = async (expenseId) => {
    if (!window.confirm('Bu ortak gider silinsin mi?')) return;
    await fetch(`${API}/shared-expenses/${expenseId}`, { method: 'DELETE', headers });
    toast.success('Ortak gider silindi');
    loadDuesSummary(selectedApt.id, selectedMonth);
  };

  // Dues Update
  const openDuesUpdateDialog = () => {
    setDuesForm({
      new_amount: selectedApt?.monthly_dues || duesSummary?.monthly_dues || '',
      period: 'aylik',
      start_month: selectedMonth
    });
    setShowDuesUpdateDialog(true);
  };

  const saveDuesUpdate = async (e) => {
    e.preventDefault();
    if (!duesForm.new_amount || parseFloat(duesForm.new_amount) <= 0) { toast.error('Yeni aidat tutarı gerekli'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/apartments/${selectedApt.id}/update-dues`, {
        method: 'POST', headers,
        body: JSON.stringify({
          apartment_id: selectedApt.id,
          new_amount: parseFloat(duesForm.new_amount),
          period: duesForm.period,
          start_month: duesForm.start_month
        })
      });
      if (!res.ok) throw new Error('Hata');
      const result = await res.json();
      toast.success(result.message);
      setShowDuesUpdateDialog(false);
      setSelectedApt({ ...selectedApt, monthly_dues: parseFloat(duesForm.new_amount) });
      loadAptDetail(selectedApt.id);
      loadDuesSummary(selectedApt.id, selectedMonth);
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const changeMonth = (delta) => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  // Tahsilat (Collection)
  const openTahsilatDialog = (preselectedUnitId = '') => {
    setTahsilatForm({
      unit_id: preselectedUnitId,
      tutar: 0,
      payment_method: 'nakit',
      note: ''
    });
    setShowTahsilatDialog(true);
  };

  const tahsilatUnitChange = (unitId) => {
    const unit = (duesSummary?.units || []).find(u => u.unit_id === unitId);
    if (!unit) { setTahsilatForm(p => ({ ...p, unit_id: unitId, tutar: 0 })); return; }
    // Otomatik öneri: aidat + ortak gider + geçmiş borç toplamı
    const aidatMonthly = unit.monthly_dues ?? selectedApt?.monthly_dues ?? duesSummary?.monthly_dues ?? 0;
    const aidatTutar = unit.is_paid ? 0 : aidatMonthly;
    const ogTutar = unit.ortak_gider_paid ? 0 : (unit.ortak_gider_borc || 0);
    const gecmisTutar = unit.past_debt || 0;
    const onerilen = aidatTutar + ogTutar + gecmisTutar;
    setTahsilatForm(p => ({ ...p, unit_id: unitId, tutar: onerilen }));
  };

  const printMakbuz = (data) => {
    const dateStr = new Date().toLocaleDateString('tr-TR');
    const timeStr = new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    const odemeLabel = { nakit: 'Nakit', kart: 'Kart', havale: 'Havale/EFT' }[data.payment_method] || data.payment_method;
    const distLabels = { aidat: 'Aidat', ortak_gider: 'Ortak Gider', ek_borc: 'Ek Borç', kredi: 'Kredi (Alacak)' };
    const distRows = (data.distributions || []).map(d => {
      const label = distLabels[d.type] || d.type;
      const detail = d.month ? `${d.month}` : (d.description || '-');
      const status = d.fully_paid ? '✓' : '◐';
      return `<tr><td>${label}</td><td>${detail}</td><td style="text-align:right">${d.amount.toLocaleString('tr-TR')} TL</td><td style="text-align:center">${status}</td></tr>`;
    }).join('');
    
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Tahsilat Makbuzu</title>
<style>
@page { size: A4; margin: 18mm; }
body { font-family: Arial, sans-serif; color: #1e293b; max-width: 700px; margin: 0 auto; }
.header { text-align: center; border-bottom: 3px solid #059669; padding-bottom: 15px; margin-bottom: 25px; }
.header h1 { color: #059669; margin: 0; font-size: 26px; }
.header p { color: #64748b; margin: 5px 0 0; font-size: 13px; }
.makbuz-info { background: #f0fdf4; border: 1px solid #86efac; padding: 8px 12px; border-radius: 6px; display: inline-block; margin-bottom: 18px; font-size: 12px; }
.row { display: flex; padding: 10px 0; border-bottom: 1px solid #e2e8f0; }
.row .label { font-weight: bold; color: #475569; width: 35%; font-size: 13px; }
.row .value { color: #1e293b; flex: 1; font-size: 13px; }
.dist-table { width: 100%; border-collapse: collapse; margin: 18px 0; }
.dist-table th { background: #f1f5f9; padding: 8px; text-align: left; font-size: 12px; color: #475569; border-bottom: 2px solid #cbd5e1; }
.dist-table td { padding: 8px; border-bottom: 1px solid #e2e8f0; font-size: 12px; }
.amount-box { background: #f0fdf4; border: 2px solid #10b981; padding: 18px; border-radius: 10px; text-align: center; margin: 22px 0; }
.amount-box .label { color: #047857; font-size: 13px; margin-bottom: 6px; }
.amount-box .amount { color: #059669; font-size: 36px; font-weight: bold; }
.balance-box { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 18px 0; }
.balance-box > div { padding: 12px; border-radius: 8px; text-align: center; font-size: 13px; }
.balance-box .credit { background: #f0fdf4; border: 1px solid #86efac; color: #047857; }
.balance-box .debt { background: #fef2f2; border: 1px solid #fca5a5; color: #b91c1c; }
.note-box { background: #fef3c7; border-left: 4px solid #f59e0b; padding: 10px 14px; margin: 18px 0; font-size: 12px; color: #78350f; border-radius: 4px; }
.signatures { display: flex; justify-content: space-between; margin-top: 50px; padding-top: 18px; }
.signature-box { width: 45%; text-align: center; border-top: 2px solid #1e293b; padding-top: 6px; font-size: 12px; }
.footer { text-align: center; margin-top: 35px; padding-top: 12px; border-top: 1px solid #e2e8f0; font-size: 10px; color: #94a3b8; }
@media print { .no-print { display: none; } }
</style></head>
<body>
<div class="header">
  <h1>TAHSİLAT MAKBUZU</h1>
  <p>${selectedApt.name}${selectedApt.address ? ' - ' + selectedApt.address : ''}</p>
</div>

<div class="makbuz-info">Makbuz No: <strong>${(data.receipt_id || '').slice(0, 8).toUpperCase()}</strong>  ·  Tarih: <strong>${dateStr} ${timeStr}</strong></div>

<div class="row"><div class="label">Daire No</div><div class="value">${data.unit_no}</div></div>
<div class="row"><div class="label">Sakin Adı</div><div class="value">${data.resident_name || '-'}</div></div>
<div class="row"><div class="label">Telefon</div><div class="value">${data.resident_phone || '-'}</div></div>
<div class="row"><div class="label">Ödeme Yöntemi</div><div class="value">${odemeLabel}</div></div>
${data.note ? `<div class="row"><div class="label">Not</div><div class="value">${data.note}</div></div>` : ''}

<div class="amount-box">
  <div class="label">TAHSİL EDİLEN TOPLAM TUTAR</div>
  <div class="amount">${data.total_paid.toLocaleString('tr-TR')} TL</div>
</div>

<h3 style="font-size:14px;margin:15px 0 5px;color:#475569">Ödeme Dağılımı</h3>
<table class="dist-table">
  <thead><tr><th>Tür</th><th>Detay</th><th style="text-align:right">Tutar</th><th style="text-align:center">Durum</th></tr></thead>
  <tbody>${distRows}</tbody>
</table>

<div class="balance-box">
  ${data.new_balance?.credit_balance > 0 ? `<div class="credit"><b>Daire Alacağı / Kredi</b><br><span style="font-size:18px">${data.new_balance.credit_balance.toLocaleString('tr-TR')} TL</span></div>` : '<div></div>'}
  ${data.new_balance?.total_borc > 0 ? `<div class="debt"><b>Kalan Borç</b><br><span style="font-size:18px">${data.new_balance.total_borc.toLocaleString('tr-TR')} TL</span></div>` : `<div class="credit"><b>Borç Durumu</b><br><span style="font-size:18px">Borç Yok ✓</span></div>`}
</div>

<div class="note-box">
  Bu makbuz, yukarıda belirtilen tutarın <strong>${selectedApt.name}</strong> yönetimi tarafından tahsil edildiğini belgeler.
</div>

<div class="signatures">
  <div class="signature-box">Ödemeyi Yapan</div>
  <div class="signature-box">Tahsil Eden / Yönetici</div>
</div>

<div class="footer">AkıllıÇözüm · Apartman Yönetim Sistemi · ${dateStr}</div>

<script>setTimeout(() => window.print(), 300);</script>
</body></html>`;
    const w = window.open('', '_blank', 'width=900,height=900');
    w.document.write(html);
    w.document.close();
  };

  const saveTahsilat = async (e) => {
    e.preventDefault();
    if (!tahsilatForm.unit_id) { toast.error('Daire seçin'); return; }
    if (!tahsilatForm.tutar || tahsilatForm.tutar <= 0) { toast.error('Tutar 0\'dan büyük olmalı'); return; }
    const unit = (duesSummary?.units || []).find(u => u.unit_id === tahsilatForm.unit_id);
    if (!unit) { toast.error('Daire bulunamadı'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/payments/smart-collect`, {
        method: 'POST', headers,
        body: JSON.stringify({
          apartment_id: selectedApt.id,
          unit_id: tahsilatForm.unit_id,
          amount: tahsilatForm.tutar,
          payment_method: tahsilatForm.payment_method,
          note: tahsilatForm.note || ''
        })
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Tahsilat kaydedilemedi');
      }
      const result = await res.json();
      toast.success('Tahsilat kaydedildi - makbuz açılıyor...');
      printMakbuz({
        receipt_id: result.receipt_id,
        unit_no: unit.unit_no,
        resident_name: unit.resident_name,
        resident_phone: unit.resident_phone,
        total_paid: result.total_paid,
        distributions: result.distributions,
        new_balance: result.new_balance,
        payment_method: tahsilatForm.payment_method,
        note: tahsilatForm.note
      });
      setShowTahsilatDialog(false);
      loadDuesSummary(selectedApt.id, selectedMonth);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Daire detay (yeni full modal)
  const openUnitDetailFull = async (unitId) => {
    setShowUnitDetailDialog(true);
    setLoadingUnitDetail(true);
    try {
      const res = await fetch(`${API}/units/${unitId}/detail`, { headers });
      if (res.ok) setUnitDetail(await res.json());
    } finally { setLoadingUnitDetail(false); }
  };

  const printUnitReport = (detail) => {
    const dateStr = new Date().toLocaleDateString('tr-TR');
    const u = detail.unit;
    const apt = detail.apartment;
    const bal = detail.balance;
    
    const duesRows = (detail.dues_history || []).slice(0, 100).map(d => {
      const tip = d.type === 'ortak_gider' ? 'Ortak Gider' : 'Aidat';
      const durum = d.is_paid ? '✓ Ödendi' : '✗ Ödenmedi';
      const tarihEklendi = (d.created_at || '').slice(0, 10);
      const tarihOdendi = d.is_paid ? (d.paid_at || '').slice(0, 10) : '-';
      return `<tr><td>${d.month}</td><td>${tip}</td><td style="text-align:right">${(d.amount || 0).toLocaleString('tr-TR')} TL</td><td style="text-align:center;color:${d.is_paid ? '#059669' : '#dc2626'}">${durum}</td><td>${tarihOdendi}</td></tr>`;
    }).join('');
    
    const extraRows = (detail.extra_debts || []).map(e => {
      const durum = e.is_paid ? '✓ Ödendi' : '✗ Ödenmedi';
      return `<tr><td>${(e.created_at || '').slice(0, 10)}</td><td>${e.description}</td><td style="text-align:right">${(e.amount || 0).toLocaleString('tr-TR')} TL</td><td style="text-align:center;color:${e.is_paid ? '#059669' : '#dc2626'}">${durum}</td></tr>`;
    }).join('');
    
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Daire Özet Raporu</title>
<style>
@page { size: A4; margin: 15mm; }
body { font-family: Arial, sans-serif; color: #1e293b; }
.header { text-align: center; border-bottom: 3px solid #2563eb; padding-bottom: 12px; margin-bottom: 20px; }
.header h1 { color: #2563eb; margin: 0; font-size: 22px; }
.header p { color: #64748b; margin: 4px 0 0; font-size: 13px; }
.info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 18px; }
.info-card { background: #f8fafc; border-left: 4px solid #2563eb; padding: 10px 14px; border-radius: 4px; }
.info-card .l { font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
.info-card .v { font-size: 14px; color: #1e293b; font-weight: bold; margin-top: 3px; }
.balance { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 18px 0; }
.balance > div { padding: 12px; border-radius: 8px; text-align: center; }
.balance .b1 { background: #fef2f2; border: 1px solid #fecaca; }
.balance .b2 { background: #fef3c7; border: 1px solid #fde68a; }
.balance .b3 { background: #fee2e2; border: 1px solid #fecaca; }
.balance .b4 { background: #dcfce7; border: 1px solid #86efac; }
.balance .lbl { font-size: 10px; color: #64748b; }
.balance .val { font-size: 16px; font-weight: bold; margin-top: 4px; color: #1e293b; }
.summary-box { background: ${bal.net_balance >= 0 ? '#f0fdf4' : '#fef2f2'}; border: 2px solid ${bal.net_balance >= 0 ? '#10b981' : '#ef4444'}; padding: 16px; border-radius: 10px; text-align: center; margin: 20px 0; }
.summary-box .lbl { color: #475569; font-size: 12px; }
.summary-box .val { font-size: 28px; font-weight: bold; margin-top: 4px; color: ${bal.net_balance >= 0 ? '#059669' : '#b91c1c'}; }
h2 { color: #475569; font-size: 14px; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px; margin: 22px 0 8px; }
table { width: 100%; border-collapse: collapse; font-size: 11px; }
table th { background: #f1f5f9; padding: 6px 8px; text-align: left; color: #475569; border-bottom: 2px solid #cbd5e1; }
table td { padding: 6px 8px; border-bottom: 1px solid #e2e8f0; }
.footer { text-align: center; margin-top: 25px; padding-top: 10px; border-top: 1px solid #e2e8f0; font-size: 10px; color: #94a3b8; }
</style></head>
<body>
<div class="header">
  <h1>DAİRE ÖZET RAPORU</h1>
  <p>${apt?.name || ''} ${apt?.address ? '· ' + apt.address : ''}</p>
</div>

<div class="info-grid">
  <div class="info-card"><div class="l">Daire No</div><div class="v">${u.unit_no}</div></div>
  <div class="info-card"><div class="l">Sakin</div><div class="v">${u.resident_name || '-'} ${u.unit_type === 'kira' ? '(Kiracı)' : '(Malik)'}</div></div>
  <div class="info-card"><div class="l">Telefon</div><div class="v">${u.resident_phone || '-'}</div></div>
  <div class="info-card"><div class="l">Rapor Tarihi</div><div class="v">${dateStr}</div></div>
</div>

<div class="balance">
  <div class="b1"><div class="lbl">Aidat Borcu</div><div class="val">${bal.aidat_borc.toLocaleString('tr-TR')} TL</div></div>
  <div class="b2"><div class="lbl">Ortak Gider Borcu</div><div class="val">${bal.ortak_gider_borc.toLocaleString('tr-TR')} TL</div></div>
  <div class="b3"><div class="lbl">Ek Borçlar</div><div class="val">${bal.extra_borc.toLocaleString('tr-TR')} TL</div></div>
  <div class="b4"><div class="lbl">Kredi/Alacak</div><div class="val">${bal.credit_balance.toLocaleString('tr-TR')} TL</div></div>
</div>

<div class="summary-box">
  <div class="lbl">${bal.net_balance >= 0 ? 'NET ALACAK' : 'NET BORÇ'}</div>
  <div class="val">${Math.abs(bal.net_balance).toLocaleString('tr-TR')} TL</div>
</div>

<h2>Aidat / Ortak Gider Geçmişi</h2>
<table>
  <thead><tr><th>Ay</th><th>Tür</th><th style="text-align:right">Tutar</th><th style="text-align:center">Durum</th><th>Ödenme Tarihi</th></tr></thead>
  <tbody>${duesRows || '<tr><td colspan="5" style="text-align:center;color:#94a3b8;padding:20px">Kayıt yok</td></tr>'}</tbody>
</table>

${(detail.extra_debts || []).length > 0 ? `
<h2>Ek Borçlar</h2>
<table>
  <thead><tr><th>Tarih</th><th>Açıklama</th><th style="text-align:right">Tutar</th><th style="text-align:center">Durum</th></tr></thead>
  <tbody>${extraRows}</tbody>
</table>` : ''}

<div class="footer">AkıllıÇözüm · Apartman Yönetim Sistemi · ${dateStr}</div>
<script>setTimeout(() => window.print(), 300);</script>
</body></html>`;
    const w = window.open('', '_blank', 'width=900,height=900');
    w.document.write(html);
    w.document.close();
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  // Apartment List View
  if (!selectedApt) {
    return (
      <div className="space-y-4" data-testid="apartment-list">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-800">Apartman Yönetimi</h1>
          <Button onClick={() => openAptDialog()} data-testid="add-apartment-btn">
            <Plus className="h-4 w-4 mr-1" />Yeni Apartman
          </Button>
        </div>

        {apartments.length === 0 ? (
          <Card><CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">Henüz apartman eklenmemiş</p>
          </CardContent></Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {apartments.map(apt => (
              <Card key={apt.id} className="cursor-pointer hover:shadow-md transition"
                onClick={() => setSelectedApt(apt)} data-testid={`apt-${apt.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-800">{apt.name}</h3>
                      <p className="text-sm text-slate-500 mt-0.5">{apt.address || 'Adres belirtilmemiş'}</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-400" />
                  </div>
                  <div className="flex gap-4 mt-3 text-sm">
                    <span className="text-slate-600"><Home className="h-3.5 w-3.5 inline mr-1" />{apt.total_units} Daire</span>
                    <span className="text-emerald-600"><DollarSign className="h-3.5 w-3.5 inline mr-1" />{apt.monthly_dues} TL/ay</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Apartment Dialog */}
        <Dialog open={showAptDialog} onOpenChange={setShowAptDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>{editingApt ? 'Apartman Düzenle' : 'Yeni Apartman'}</DialogTitle></DialogHeader>
            <form onSubmit={saveApartment} className="space-y-3">
              <div className="space-y-1"><Label>Apartman / Site Adı *</Label>
                <Input value={aptForm.name} onChange={e => setAptForm({...aptForm, name: e.target.value})} data-testid="apt-name-input" /></div>
              <div className="space-y-1"><Label>Adres</Label>
                <Input value={aptForm.address} onChange={e => setAptForm({...aptForm, address: e.target.value})} /></div>
              <div className="space-y-1"><Label>Toplam Daire</Label>
                <Input type="number" value={aptForm.total_units} onChange={e => setAptForm({...aptForm, total_units: parseInt(e.target.value) || 0})} /></div>
              <div className="space-y-1"><Label>Not</Label>
                <Input value={aptForm.note} onChange={e => setAptForm({...aptForm, note: e.target.value})} /></div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowAptDialog(false)} className="flex-1">İptal</Button>
                <Button type="submit" disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kaydet'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Apartment Detail View
  const units = aptDetail?.units || [];
  const allSummaryUnits = duesSummary?.units || [];
  const summaryUnits = allSummaryUnits.filter(u => {
    if (duesFilter === 'borcu_olan') return !u.is_paid || !u.ortak_gider_paid || (u.past_debt || 0) > 0;
    if (duesFilter === 'borcu_yok') return u.is_paid && u.ortak_gider_paid && (u.past_debt || 0) === 0;
    return true;
  });
  const monthNames = ['Ocak','Subat','Mart','Nisan','Mayis','Haziran','Temmuz','Agustos','Eylul','Ekim','Kasim','Aralik'];
  const [sy, sm] = selectedMonth.split('-').map(Number);
  const monthLabel = `${monthNames[sm - 1]} ${sy}`;

  return (
    <div className="space-y-4" data-testid="apartment-detail">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => { setSelectedApt(null); setAptDetail(null); setDuesSummary(null); }}>
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-800">{selectedApt.name}</h1>
          <p className="text-sm text-slate-500">{selectedApt.address}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => openAptDialog(selectedApt)}><Edit2 className="h-3.5 w-3.5 mr-1" />Düzenle</Button>
        <Button variant="outline" size="sm" className="text-red-500" onClick={() => deleteApartment(selectedApt)}><Trash2 className="h-3.5 w-3.5 mr-1" />Sil</Button>
      </div>

      {/* Üst özet kartları (Toplam Daire/Aidat/Ödeme Yapan/Borcu Olan/Toplanan) sadelik için kaldırıldı.
          Daire bazlı detaylar tablo üzerinde gösteriliyor. */}

      {/* Ay/Yıl Özeti - sadelik için kaldırıldı, detaylar daire bazında gösteriliyor */}

      {/* Month Selector + Actions */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => changeMonth(-1)}><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm font-medium w-28 text-center">{monthLabel}</span>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => changeMonth(1)}><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={openExpenseDialog} data-testid="add-expense-btn"
            className="text-amber-700 border-amber-300 hover:bg-amber-50">
            <Wrench className="h-3.5 w-3.5 mr-1" />Ortak Gider
          </Button>
          <Button size="sm" variant="outline" onClick={openDuesUpdateDialog} data-testid="update-dues-btn"
            className="text-blue-700 border-blue-300 hover:bg-blue-50">
            <RefreshCw className="h-3.5 w-3.5 mr-1" />Aidat Güncelle
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowEvrakSection(s => !s)}
            className="text-purple-700 border-purple-300 hover:bg-purple-50" data-testid="evrak-btn">
            <FileText className="h-3.5 w-3.5 mr-1" />Evraklar
          </Button>
          <Button size="sm" onClick={openTahsilatDialog} data-testid="tahsilat-btn"
            className="bg-emerald-600 hover:bg-emerald-700">
            <CreditCard className="h-4 w-4 mr-1" />Tahsilat / Borç
          </Button>
          <Button size="sm" onClick={() => openUnitDialog()} data-testid="add-unit-btn">
            <Plus className="h-4 w-4 mr-1" />Daire Ekle
          </Button>
        </div>
      </div>

      {/* Filtre Butonları */}
      <div className="flex gap-1 flex-wrap">
        <Button variant={duesFilter === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setDuesFilter('all')}>
          Tümü ({allSummaryUnits.length})
        </Button>
        <Button variant={duesFilter === 'borcu_olan' ? 'default' : 'outline'} size="sm"
          className={duesFilter === 'borcu_olan' ? 'bg-red-600 hover:bg-red-700' : 'text-red-600 border-red-200'}
          onClick={() => setDuesFilter('borcu_olan')}>
          Borcu Olan ({allSummaryUnits.filter(u => !u.is_paid || !u.ortak_gider_paid || (u.past_debt || 0) > 0).length})
        </Button>
        <Button variant={duesFilter === 'borcu_yok' ? 'default' : 'outline'} size="sm"
          className={duesFilter === 'borcu_yok' ? 'bg-emerald-600 hover:bg-emerald-700' : 'text-emerald-600 border-emerald-200'}
          onClick={() => setDuesFilter('borcu_yok')}>
          Borcu Yok ({allSummaryUnits.filter(u => u.is_paid && u.ortak_gider_paid && (u.past_debt || 0) === 0).length})
        </Button>
      </div>

      {/* Evraklar (Documents) Section */}
      {showEvrakSection && <EvrakSection apartmentId={selectedApt.id} headers={headers} token={token} />}

      {/* Shared Expenses for this month */}
      {duesSummary?.shared_expenses?.length > 0 && (
        <Card data-testid="shared-expenses-section">
          <CardContent className="p-4">
            <h3 className="font-medium text-sm text-amber-700 mb-2 flex items-center gap-2">
              <Wrench className="h-4 w-4" />Ortak Giderler - {monthLabel}
            </h3>
            <div className="space-y-2">
              {duesSummary.shared_expenses.map(exp => (
                <div key={exp.id} className="flex items-center justify-between p-2 bg-amber-50 rounded-lg border border-amber-200">
                  <div>
                    <p className="text-sm font-medium">{exp.description}</p>
                    <p className="text-xs text-slate-500">
                      {exp.category && <span className="mr-2">{getCategoryLabel(exp.category)}</span>}
                      Daire başı: {(exp.per_unit_amount || 0).toLocaleString('tr-TR')} TL ({exp.unit_count} daire)
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-amber-700">{(exp.amount || 0).toLocaleString('tr-TR')} TL</span>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => deleteExpense(exp.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Units / Dues Table */}
      <div className="bg-white rounded-lg border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left p-3 font-medium text-slate-600">Daire</th>
              <th className="text-left p-3 font-medium text-slate-600">Kat</th>
              <th className="text-left p-3 font-medium text-slate-600">Sakin Adı</th>
              <th className="text-left p-3 font-medium text-slate-600">Telefon</th>
              <th className="text-center p-3 font-medium text-slate-600">Tür</th>
              <th className="text-center p-3 font-medium text-slate-600">Bu Ayki Borç</th>
              <th className="text-center p-3 font-medium text-slate-600">Geçmiş Borç</th>
              <th className="text-center p-3 font-medium text-emerald-700">Alacak</th>
              <th className="text-right p-3 font-medium text-slate-600">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {summaryUnits.length > 0 ? summaryUnits.map(u => {
              const aidatBorc = u.is_paid ? 0 : Math.max(0, (u.monthly_dues || 0) - (u.total_paid || 0));
              const ogBorc = u.ortak_gider_paid ? 0 : (u.ortak_gider_borc || 0);
              const buAyBorc = aidatBorc + ogBorc;
              const credit = u.credit_balance || 0;
              return (
              <tr key={u.unit_id} className="border-b hover:bg-slate-50" data-testid={`unit-row-${u.unit_no}`}>
                <td className="p-3 font-medium">
                  <button className="text-blue-600 hover:underline cursor-pointer" onClick={() => openUnitDetailFull(u.unit_id)}>
                    {u.unit_no}
                  </button>
                </td>
                <td className="p-3 text-slate-500">{u.floor || '-'}</td>
                <td className="p-3">
                  {u.resident_name ? (
                    <button className="text-slate-700 hover:text-blue-600 hover:underline cursor-pointer text-left" onClick={() => openUnitDetailFull(u.unit_id)}>
                      {u.resident_name}
                    </button>
                  ) : <span className="text-slate-400">Boş</span>}
                </td>
                <td className="p-3 text-slate-500">{u.resident_phone || '-'}</td>
                <td className="p-3 text-center">
                  <span className={`px-1.5 py-0.5 rounded text-xs ${u.unit_type === 'kira' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
                    {u.unit_type === 'kira' ? 'Kira' : 'Malik'}
                  </span>
                </td>
                <td className="p-3 text-center">
                  {buAyBorc > 0 ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-red-100 text-red-700 font-medium">
                      <X className="h-3 w-3" />{buAyBorc.toLocaleString('tr-TR')} TL
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-emerald-100 text-emerald-700">
                      <Check className="h-3 w-3" />Ödendi
                    </span>
                  )}
                </td>
                <td className="p-3 text-center">
                  {(u.past_debt || 0) > 0 ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-red-100 text-red-700 font-medium">
                      <X className="h-3 w-3" />{u.past_debt.toLocaleString('tr-TR')} TL
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400">-</span>
                  )}
                </td>
                <td className="p-3 text-center">
                  {credit > 0 ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-emerald-100 text-emerald-700 font-bold">
                      {credit.toLocaleString('tr-TR')} TL
                    </span>
                  ) : (
                    <span className="text-xs text-slate-300">-</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => {
                      const fullUnit = units.find(x => x.id === u.unit_id);
                      if (fullUnit) openUnitDialog(fullUnit);
                    }}><Edit2 className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => deleteUnit({ id: u.unit_id, unit_no: u.unit_no })}>
                      <Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </td>
              </tr>
              );
            }) : units.length > 0 ? units.map(u => (
              <tr key={u.id} className="border-b hover:bg-slate-50">
                <td className="p-3 font-medium">{u.unit_no}</td>
                <td className="p-3 text-slate-500">{u.floor || '-'}</td>
                <td className="p-3">{u.resident_name || '-'}</td>
                <td className="p-3 text-slate-500">{u.resident_phone || '-'}</td>
                <td className="p-3 text-center">{u.unit_type === 'kira' ? 'Kira' : 'Malik'}</td>
                <td className="p-3 text-center">-</td>
                <td className="p-3 text-center">-</td>
                <td className="p-3 text-center">-</td>
                <td className="p-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openUnitDialog(u)}><Edit2 className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => deleteUnit(u)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </td>
              </tr>
            )) : (
              <tr><td colSpan={9} className="p-8 text-center text-slate-400">Henüz daire eklenmemiş</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Unit Dialog */}
      <Dialog open={showUnitDialog} onOpenChange={setShowUnitDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingUnit ? 'Daire Düzenle' : 'Yeni Daire'}</DialogTitle></DialogHeader>
          <form onSubmit={saveUnit} className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label>Daire No *</Label>
                <Input value={unitForm.unit_no} onChange={e => setUnitForm({...unitForm, unit_no: e.target.value})} data-testid="unit-no-input" /></div>
              <div className="space-y-1"><Label>Kat</Label>
                <Input value={unitForm.floor} onChange={e => setUnitForm({...unitForm, floor: e.target.value})} /></div>
              <div className="space-y-1"><Label>m2</Label>
                <Input type="number" value={unitForm.area_m2} onChange={e => setUnitForm({...unitForm, area_m2: parseFloat(e.target.value) || 0})} /></div>
            </div>
            <div className="space-y-1"><Label>Tur</Label>
              <select value={unitForm.unit_type} onChange={e => setUnitForm({...unitForm, unit_type: e.target.value})}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                <option value="malik">Malik</option>
                <option value="kira">Kira</option>
              </select>
            </div>
            <div className="space-y-1"><Label>Sakin Adı</Label>
              <Input value={unitForm.resident_name} onChange={e => setUnitForm({...unitForm, resident_name: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Telefon</Label>
                <Input value={unitForm.resident_phone} onChange={e => setUnitForm({...unitForm, resident_phone: e.target.value})} /></div>
              <div className="space-y-1"><Label>Telefon 2</Label>
                <Input value={unitForm.resident_phone2} onChange={e => setUnitForm({...unitForm, resident_phone2: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Giris Tarihi</Label>
                <Input type="date" value={unitForm.move_in_date} onChange={e => setUnitForm({...unitForm, move_in_date: e.target.value})} /></div>
              <div className="space-y-1"><Label>Not</Label>
                <Input value={unitForm.note} onChange={e => setUnitForm({...unitForm, note: e.target.value})} /></div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowUnitDialog(false)} className="flex-1">İptal</Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kaydet'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Apartment Edit Dialog */}
      <Dialog open={showAptDialog} onOpenChange={setShowAptDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Apartman Düzenle</DialogTitle></DialogHeader>
          <form onSubmit={saveApartment} className="space-y-3">
            <div className="space-y-1"><Label>Apartman Adı *</Label>
              <Input value={aptForm.name} onChange={e => setAptForm({...aptForm, name: e.target.value})} /></div>
            <div className="space-y-1"><Label>Adres</Label>
              <Input value={aptForm.address} onChange={e => setAptForm({...aptForm, address: e.target.value})} /></div>
            <div className="space-y-1"><Label>Toplam Daire</Label>
              <Input type="number" value={aptForm.total_units} onChange={e => setAptForm({...aptForm, total_units: parseInt(e.target.value) || 0})} /></div>
            <div className="space-y-1"><Label>Not</Label>
              <Input value={aptForm.note} onChange={e => setAptForm({...aptForm, note: e.target.value})} /></div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowAptDialog(false)} className="flex-1">İptal</Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kaydet'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tahsilat (Collection) Dialog */}
      <Dialog open={showTahsilatDialog} onOpenChange={setShowTahsilatDialog}>
        <DialogContent className="max-w-md" data-testid="tahsilat-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-emerald-600" />
              Tahsilat / Borç Ödemesi
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={saveTahsilat} className="space-y-3">
            <div className="space-y-1">
              <Label>Daire Seçin *</Label>
              <select value={tahsilatForm.unit_id} onChange={e => tahsilatUnitChange(e.target.value)}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="tahsilat-unit-select">
                <option value="">-- Daire seçin --</option>
                {(duesSummary?.units || []).map(u => (
                  <option key={u.unit_id} value={u.unit_id}>
                    Daire {u.unit_no}{u.resident_name ? ' · ' + u.resident_name : ' · (Boş)'}
                    {u.unit_type === 'kira' ? ' · Kiracı' : ' · Malik'}
                  </option>
                ))}
              </select>
            </div>

            {tahsilatForm.unit_id && (() => {
              const unit = (duesSummary?.units || []).find(u => u.unit_id === tahsilatForm.unit_id);
              if (!unit) return null;
              const aidatTutar = unit.monthly_dues ?? selectedApt?.monthly_dues ?? duesSummary?.monthly_dues ?? 0;
              const aidatBorc = unit.is_paid ? 0 : aidatTutar;
              const ogBorc = unit.ortak_gider_paid ? 0 : (unit.ortak_gider_borc || 0);
              const gecmis = unit.past_debt || 0;
              const toplamBorc = aidatBorc + ogBorc + gecmis;
              return (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-1.5 text-xs">
                  <div className="flex justify-between"><span className="text-slate-600">Bu Ay Aidat:</span>
                    {aidatBorc > 0
                      ? <span className="text-red-600 font-medium">{aidatTutar.toLocaleString('tr-TR')} TL</span>
                      : <span className="text-emerald-600 font-medium">✓ Ödendi</span>}
                  </div>
                  <div className="flex justify-between"><span className="text-slate-600">Bu Ay Ortak Gider:</span>
                    {ogBorc > 0
                      ? <span className="text-amber-600 font-medium">{ogBorc.toLocaleString('tr-TR')} TL</span>
                      : <span className="text-emerald-600 font-medium">{(unit.ortak_gider_borc || 0) > 0 ? '✓ Ödendi' : 'Yok'}</span>}
                  </div>
                  <div className="flex justify-between"><span className="text-slate-600">Geçmiş Borç:</span>
                    {gecmis > 0
                      ? <span className="text-red-600 font-medium">{gecmis.toLocaleString('tr-TR')} TL</span>
                      : <span className="text-emerald-600 font-medium">Yok</span>}
                  </div>
                  <div className="flex justify-between pt-1.5 border-t border-emerald-300 font-bold">
                    <span className="text-emerald-800">Toplam Borç:</span>
                    <span className={toplamBorc > 0 ? 'text-red-700' : 'text-emerald-700'}>{toplamBorc.toLocaleString('tr-TR')} TL</span>
                  </div>
                </div>
              );
            })()}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 text-[11px] text-blue-700">
              💡 <b>Akıllı Dağıtım:</b> Girilen tutar otomatik olarak şu sırayla dağıtılır: <b>Aidat (eski → yeni)</b> → <b>Ortak Gider</b> → <b>Ek Borçlar</b>. Fazla ödeme <b>kredi/alacak</b> olarak kaydedilir.
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Tutar (TL) *</Label>
                <Input type="number" step="0.01" value={tahsilatForm.tutar}
                  onChange={e => setTahsilatForm({...tahsilatForm, tutar: parseFloat(e.target.value) || 0})}
                  data-testid="tahsilat-tutar" autoFocus /></div>
              <div className="space-y-1"><Label>Ödeme Yöntemi</Label>
                <select value={tahsilatForm.payment_method}
                  onChange={e => setTahsilatForm({...tahsilatForm, payment_method: e.target.value})}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                  <option value="nakit">Nakit</option>
                  <option value="kart">Kart</option>
                  <option value="havale">Havale/EFT</option>
                </select>
              </div>
            </div>

            <div className="space-y-1"><Label>Not (opsiyonel)</Label>
              <Input value={tahsilatForm.note} onChange={e => setTahsilatForm({...tahsilatForm, note: e.target.value})}
                placeholder="Açıklama..." /></div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowTahsilatDialog(false)} className="flex-1">İptal</Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="tahsilat-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                  <><Check className="h-4 w-4 mr-1" />Tahsil Et & Yazdır</>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Daire Detay Dialog */}
      <Dialog open={showUnitDetailDialog} onOpenChange={setShowUnitDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="unit-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Home className="h-5 w-5 text-blue-600" />
              {unitDetail ? `Daire ${unitDetail.unit?.unit_no} · ${unitDetail.unit?.resident_name || 'Boş'}` : 'Daire Detayı'}
            </DialogTitle>
          </DialogHeader>
          {loadingUnitDetail ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-emerald-500" /></div>
          ) : unitDetail ? (
            <UnitDetailView detail={unitDetail} onPay={() => {
              setShowUnitDetailDialog(false);
              openTahsilatDialog(unitDetail.unit?.id);
            }} onAddDebt={() => setShowExtraDebtForm(true)}
            onPrintReport={() => printUnitReport(unitDetail)}
            onDeleteDebt={async (debtId) => {
              if (!window.confirm('Bu borç silinsin mi?')) return;
              await fetch(`${API}/extra-debts/${debtId}`, { method: 'DELETE', headers });
              toast.success('Silindi');
              await openUnitDetailFull(unitDetail.unit.id);
            }} />
          ) : null}

          {/* Ek Borç Ekleme Form */}
          <Dialog open={showExtraDebtForm} onOpenChange={setShowExtraDebtForm}>
            <DialogContent className="max-w-sm">
              <DialogHeader><DialogTitle>Daireye Ek Borç Ekle</DialogTitle></DialogHeader>
              <form onSubmit={async (e) => {
                e.preventDefault();
                if (!extraDebtForm.description || !extraDebtForm.amount) { toast.error('Açıklama ve tutar gerekli'); return; }
                const res = await fetch(`${API}/units/${unitDetail.unit.id}/extra-debts`, {
                  method: 'POST', headers, body: JSON.stringify(extraDebtForm)
                });
                if (res.ok) {
                  toast.success('Borç eklendi');
                  setShowExtraDebtForm(false);
                  setExtraDebtForm({ description: '', amount: 0, category: 'diger' });
                  await openUnitDetailFull(unitDetail.unit.id);
                  loadDuesSummary(selectedApt.id, selectedMonth);
                } else toast.error('Eklenemedi');
              }} className="space-y-3">
                <div className="space-y-1"><Label>Açıklama *</Label>
                  <Input value={extraDebtForm.description} onChange={e => setExtraDebtForm({...extraDebtForm, description: e.target.value})}
                    placeholder="Örn: Kapı tamiri, gecikme cezası..." autoFocus /></div>
                <div className="space-y-1"><Label>Tutar (TL) *</Label>
                  <Input type="number" step="0.01" value={extraDebtForm.amount}
                    onChange={e => setExtraDebtForm({...extraDebtForm, amount: parseFloat(e.target.value) || 0})} /></div>
                <div className="space-y-1"><Label>Kategori</Label>
                  <select value={extraDebtForm.category} onChange={e => setExtraDebtForm({...extraDebtForm, category: e.target.value})}
                    className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                    <option value="diger">Diğer</option>
                    <option value="tessat">Tesisat</option>
                    <option value="onarim">Onarım</option>
                    <option value="ceza">Gecikme Cezası</option>
                    <option value="aidat_farki">Aidat Farkı</option>
                  </select></div>
                <div className="flex gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowExtraDebtForm(false)} className="flex-1">İptal</Button>
                  <Button type="submit" className="flex-1 bg-red-600 hover:bg-red-700">Borç Ekle</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPayDialog} onOpenChange={setShowPayDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{payType === 'ortak_gider' ? 'Ortak Gider Ödemesi' : 'Aidat Ödemesi'} - Daire {payForm.unit_no}</DialogTitle></DialogHeader>
          <form onSubmit={saveDuesPayment} className="space-y-3">
            <div className="text-sm text-slate-500">Sakin: {payForm.resident_name || '-'}</div>
            <div className="space-y-1"><Label>Tutar (TL) *</Label>
              <Input type="number" step="0.01" value={payForm.amount} onChange={e => setPayForm({...payForm, amount: parseFloat(e.target.value) || 0})} /></div>
            <div className="space-y-1"><Label>Ödeme Yöntemi</Label>
              <select value={payForm.payment_method} onChange={e => setPayForm({...payForm, payment_method: e.target.value})}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                <option value="nakit">Nakit</option>
                <option value="kart">Kart</option>
                <option value="havale">Havale/EFT</option>
              </select>
            </div>
            <div className="space-y-1"><Label>Not</Label>
              <Input value={payForm.note} onChange={e => setPayForm({...payForm, note: e.target.value})} /></div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowPayDialog(false)} className="flex-1">İptal</Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Ödemeyi Kaydet'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Shared Expense Dialog */}
      <Dialog open={showExpenseDialog} onOpenChange={setShowExpenseDialog}>
        <DialogContent className="max-w-md" data-testid="expense-dialog">
          <DialogHeader><DialogTitle>Ortak Gider Ekle</DialogTitle></DialogHeader>
          <form onSubmit={saveExpense} className="space-y-3">
            <p className="text-sm text-slate-500">Toplam tutar tüm dairelere eşit olarak dağıtılacak.</p>
            <div className="space-y-1"><Label>Açıklama *</Label>
              <Input value={expenseForm.description} onChange={e => setExpenseForm({...expenseForm, description: e.target.value})}
                placeholder="Örn: Çatı bakımı, Asansör tamiri..." data-testid="expense-description" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Toplam Tutar (TL) *</Label>
                <Input type="number" step="0.01" value={expenseForm.amount}
                  onChange={e => setExpenseForm({...expenseForm, amount: e.target.value})}
                  placeholder="0" data-testid="expense-amount" /></div>
              <div className="space-y-1"><Label>Kategori</Label>
                <select value={expenseForm.category} onChange={e => setExpenseForm({...expenseForm, category: e.target.value})}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                  <option value="">Seçiniz</option>
                  <option value="cati_bakim">Çatı Bakım</option>
                  <option value="asansor">Asansör</option>
                  <option value="temizlik">Temizlik</option>
                  <option value="boya_badana">Boya/Badana</option>
                  <option value="tessat">Tesisat</option>
                  <option value="bahce">Bahçe</option>
                  <option value="guvenlik">Güvenlik</option>
                  <option value="diger">Diğer</option>
                </select></div>
            </div>
            <div className="space-y-1"><Label>Ay</Label>
              <Input type="month" value={expenseForm.month}
                onChange={e => setExpenseForm({...expenseForm, month: e.target.value})} /></div>
            {expenseForm.amount && aptDetail?.units?.length > 0 && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm">
                <p>Toplam: <strong>{parseFloat(expenseForm.amount).toLocaleString('tr-TR')} TL</strong></p>
                <p>Daire sayısı: <strong>{aptDetail.units.length}</strong></p>
                <p>Daire başı: <strong>{(parseFloat(expenseForm.amount) / aptDetail.units.length).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} TL</strong></p>
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowExpenseDialog(false)} className="flex-1">İptal</Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-amber-600 hover:bg-amber-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Gideri Kaydet'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dues Update Dialog */}
      <Dialog open={showDuesUpdateDialog} onOpenChange={setShowDuesUpdateDialog}>
        <DialogContent className="max-w-md" data-testid="dues-update-dialog">
          <DialogHeader><DialogTitle>Aidat Güncelle</DialogTitle></DialogHeader>
          <form onSubmit={saveDuesUpdate} className="space-y-3">
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800 font-medium">Bu aydaki mevcut aidat değişmez.</p>
              <p className="text-xs text-amber-700 mt-1">Yeni aidat tutarı bir sonraki aydan itibaren geçerli olacaktır.</p>
            </div>
            <div className="space-y-1"><Label>Yeni Aidat Tutarı (TL) *</Label>
              <Input type="number" step="0.01" value={duesForm.new_amount}
                onChange={e => setDuesForm({...duesForm, new_amount: e.target.value})}
                placeholder="0" data-testid="new-dues-amount" /></div>
            <div className="space-y-1"><Label>Periyod</Label>
              <select value={duesForm.period} onChange={e => setDuesForm({...duesForm, period: e.target.value})}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="dues-period">
                <option value="aylik">Aylık (1 ay)</option>
                <option value="3aylik">3 Aylık</option>
                <option value="6aylik">6 Aylık</option>
              </select></div>
            <div className="space-y-1"><Label>Başlangıç Ayı</Label>
              <Input type="month" value={duesForm.start_month}
                onChange={e => setDuesForm({...duesForm, start_month: e.target.value})} /></div>
            {duesForm.new_amount && aptDetail?.units?.length > 0 && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm">
                <p>Yeni aidat: <strong>{parseFloat(duesForm.new_amount).toLocaleString('tr-TR')} TL</strong></p>
                <p>Daire sayısı: <strong>{aptDetail.units.length}</strong></p>
                <p>Periyod: <strong>{{aylik: '1 ay', '3aylik': '3 ay', '6aylik': '6 ay'}[duesForm.period]}</strong></p>
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowDuesUpdateDialog(false)} className="flex-1">İptal</Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Güncelle'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Daire Detay Dialog */}
      <Dialog open={!!selectedUnit} onOpenChange={(v) => { if (!v) setSelectedUnit(null); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Daire {selectedUnit?.unit_no} - {selectedUnit?.resident_name || 'Boş'}</span>
              <Button variant="outline" size="sm" onClick={downloadUnitPdf} data-testid="unit-pdf-btn">
                <Download className="h-3.5 w-3.5 mr-1" />PDF
              </Button>
            </DialogTitle>
          </DialogHeader>

          {selectedUnit && (
            <div className="space-y-4">
              {/* Özet Kartlar */}
              <div className="grid grid-cols-3 gap-2">
                <div className="p-2 bg-blue-50 rounded-lg text-center">
                  <p className="text-[10px] text-blue-600">Aidat</p>
                  <p className="text-sm font-bold text-blue-700">{(duesSummary?.monthly_dues || 0).toLocaleString('tr-TR')} TL</p>
                  {selectedUnit.is_paid ? (
                    <span className="text-[10px] text-emerald-600 flex items-center justify-center gap-0.5"><CheckCircle className="h-3 w-3" />Ödendi</span>
                  ) : (
                    <span className="text-[10px] text-red-600">Ödenmedi</span>
                  )}
                </div>
                <div className="p-2 bg-amber-50 rounded-lg text-center">
                  <p className="text-[10px] text-amber-600">Ortak Gider</p>
                  <p className="text-sm font-bold text-amber-700">{(selectedUnit.ortak_gider_borc || 0).toLocaleString('tr-TR')} TL</p>
                  {selectedUnit.ortak_gider_paid ? (
                    <span className="text-[10px] text-emerald-600 flex items-center justify-center gap-0.5"><CheckCircle className="h-3 w-3" />Ödendi</span>
                  ) : selectedUnit.ortak_gider_borc > 0 ? (
                    <span className="text-[10px] text-red-600">Ödenmedi</span>
                  ) : null}
                </div>
                <div className="p-2 bg-red-50 rounded-lg text-center">
                  <p className="text-[10px] text-red-600">Geçmiş Borç</p>
                  <p className="text-sm font-bold text-red-700">{(selectedUnit.past_debt || 0).toLocaleString('tr-TR')} TL</p>
                </div>
              </div>

              {/* Toplam */}
              <div className="p-3 bg-slate-100 rounded-lg flex items-center justify-between">
                <span className="font-medium text-slate-700">Toplam Borç:</span>
                <span className="text-xl font-bold text-red-700">
                  {((selectedUnit.is_paid ? 0 : (duesSummary?.monthly_dues || 0)) +
                    (selectedUnit.ortak_gider_paid ? 0 : (selectedUnit.ortak_gider_borc || 0)) +
                    (selectedUnit.past_debt || 0)).toLocaleString('tr-TR')} TL
                </span>
              </div>

              {/* Ödeme Geçmişi */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 mb-2">Ödeme / Borç Geçmişi</h3>
                {loadingUnit ? (
                  <div className="py-4 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div>
                ) : unitDebts.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">Kayıt bulunamadı</p>
                ) : (
                  <div className="space-y-1 max-h-60 overflow-y-auto">
                    {unitDebts.map((d, i) => (
                      <div key={i} className={`flex items-center justify-between p-2 rounded text-sm ${d.is_paid ? 'bg-emerald-50' : 'bg-red-50'}`}>
                        <div>
                          <p className="font-medium">{d.note || (d.type === 'ortak_gider' ? 'Ortak Gider' : 'Aidat')}</p>
                          <p className="text-xs text-slate-500">{d.month} {d.paid_at ? `| Ödendi: ${new Date(d.paid_at).toLocaleDateString('tr-TR')}` : ''}</p>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${d.is_paid ? 'text-emerald-600' : 'text-red-600'}`}>{(d.amount || 0).toLocaleString('tr-TR')} TL</p>
                          <p className="text-[10px] text-slate-400">{d.is_paid ? 'Ödendi' : 'Ödenmedi'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Ödeme Butonları */}
              <div className="flex gap-2">
                {!selectedUnit.is_paid && (
                  <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => { openPayDialog(selectedUnit, 'aidat'); setSelectedUnit(null); }}>
                    <CreditCard className="h-3.5 w-3.5 mr-1" />Aidat Öde
                  </Button>
                )}
                {selectedUnit.ortak_gider_borc > 0 && !selectedUnit.ortak_gider_paid && (
                  <Button size="sm" className="flex-1 bg-amber-600 hover:bg-amber-700"
                    onClick={() => { openPayDialog(selectedUnit, 'ortak_gider'); setSelectedUnit(null); }}>
                    <CreditCard className="h-3.5 w-3.5 mr-1" />Ortak Gider Öde
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== DAİRE DETAY VIEW ====================
function UnitDetailView({ detail, onPay, onAddDebt, onPrintReport, onDeleteDebt }) {
  const u = detail.unit;
  const apt = detail.apartment;
  const bal = detail.balance;
  const [tab, setTab] = useState('ozet');
  
  const TYPE_LABELS = { aidat: 'Aidat', ortak_gider: 'Ortak Gider', ek_borc: 'Ek Borç', kredi: 'Kredi' };
  
  return (
    <div className="space-y-4">
      {/* Action Bar */}
      <div className="flex flex-wrap gap-2 pb-3 border-b">
        <Button onClick={onPay} className="bg-emerald-600 hover:bg-emerald-700" data-testid="detail-tahsilat-btn">
          <CreditCard className="h-4 w-4 mr-1.5" /> Tahsilat Yap
        </Button>
        <Button onClick={onAddDebt} variant="outline" className="text-red-700 border-red-300 hover:bg-red-50">
          <Plus className="h-4 w-4 mr-1.5" /> Ek Borç Ekle
        </Button>
        <Button onClick={onPrintReport} variant="outline" className="text-blue-700 border-blue-300 hover:bg-blue-50">
          <FileText className="h-4 w-4 mr-1.5" /> Özet Rapor (A4)
        </Button>
      </div>

      {/* Sakin Info */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div className="bg-slate-50 rounded-lg p-3">
          <div className="text-xs text-slate-500 uppercase mb-1">Daire No</div>
          <div className="font-bold text-slate-800">{u.unit_no}</div>
        </div>
        <div className="bg-slate-50 rounded-lg p-3">
          <div className="text-xs text-slate-500 uppercase mb-1">Kat</div>
          <div className="font-bold text-slate-800">{u.floor || '-'}</div>
        </div>
        <div className="bg-slate-50 rounded-lg p-3">
          <div className="text-xs text-slate-500 uppercase mb-1">Tip</div>
          <div className="font-bold text-slate-800">{u.unit_type === 'kira' ? 'Kiracı' : 'Malik'}</div>
        </div>
        <div className="bg-slate-50 rounded-lg p-3">
          <div className="text-xs text-slate-500 uppercase mb-1">Telefon</div>
          <div className="font-bold text-slate-800 text-xs">{u.resident_phone || '-'}</div>
        </div>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
          <div className="text-[10px] text-red-600 uppercase font-semibold">Aidat Borcu</div>
          <div className="text-lg font-bold text-red-700 mt-1">{bal.aidat_borc.toLocaleString('tr-TR')} TL</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
          <div className="text-[10px] text-amber-600 uppercase font-semibold">Ortak Gider Borcu</div>
          <div className="text-lg font-bold text-amber-700 mt-1">{bal.ortak_gider_borc.toLocaleString('tr-TR')} TL</div>
        </div>
        <div className="bg-pink-50 border border-pink-200 rounded-xl p-3 text-center">
          <div className="text-[10px] text-pink-600 uppercase font-semibold">Ek Borçlar</div>
          <div className="text-lg font-bold text-pink-700 mt-1">{bal.extra_borc.toLocaleString('tr-TR')} TL</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
          <div className="text-[10px] text-emerald-600 uppercase font-semibold">Kredi/Alacak</div>
          <div className="text-lg font-bold text-emerald-700 mt-1">{bal.credit_balance.toLocaleString('tr-TR')} TL</div>
        </div>
      </div>

      {/* Net Balance */}
      <div className={`rounded-xl p-4 text-center border-2 ${
        bal.net_balance >= 0 ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'
      }`}>
        <div className={`text-xs uppercase font-semibold ${bal.net_balance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
          {bal.net_balance >= 0 ? 'NET ALACAK (Daire Lehine)' : 'NET BORÇ'}
        </div>
        <div className={`text-3xl font-bold mt-1 ${bal.net_balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
          {Math.abs(bal.net_balance).toLocaleString('tr-TR')} TL
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
        {[
          { v: 'ozet', l: `Aidat Geçmişi (${detail.dues_history?.length || 0})` },
          { v: 'extra', l: `Ek Borçlar (${detail.extra_debts?.length || 0})` },
          { v: 'makbuz', l: `Makbuzlar (${detail.receipts?.length || 0})` },
        ].map(t => (
          <button key={t.v} onClick={() => setTab(t.v)}
            className={`flex-1 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
              tab === t.v ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600 hover:bg-white/50'
            }`}>
            {t.l}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'ozet' && (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="p-2 text-left">Ay</th>
                <th className="p-2 text-left">Tür</th>
                <th className="p-2 text-right">Tutar</th>
                <th className="p-2 text-center">Durum</th>
                <th className="p-2 text-left">Ödenme Tarihi</th>
                <th className="p-2 text-left">Ödeme Yöntemi</th>
              </tr>
            </thead>
            <tbody>
              {(detail.dues_history || []).slice(0, 100).map(d => (
                <tr key={d.id} className="border-t">
                  <td className="p-2 font-medium">{d.month}</td>
                  <td className="p-2">{d.type === 'ortak_gider' ? <span className="text-amber-600">Ortak Gider</span> : 'Aidat'}</td>
                  <td className="p-2 text-right font-medium">{(d.amount || 0).toLocaleString('tr-TR')} TL</td>
                  <td className="p-2 text-center">
                    {d.is_paid
                      ? <span className="text-emerald-600 font-semibold">✓ Ödendi</span>
                      : <span className="text-red-600 font-semibold">✗ Ödenmedi</span>}
                  </td>
                  <td className="p-2 text-slate-500">{d.is_paid ? (d.paid_at || '').slice(0, 10) : '-'}</td>
                  <td className="p-2 text-slate-500">
                    {d.payment_method === 'nakit' ? 'Nakit' : d.payment_method === 'kart' ? 'Kart' : d.payment_method === 'havale' ? 'Havale' : '-'}
                  </td>
                </tr>
              ))}
              {(!detail.dues_history || detail.dues_history.length === 0) && (
                <tr><td colSpan="6" className="p-6 text-center text-slate-400">Kayıt yok</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'extra' && (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="p-2 text-left">Tarih</th>
                <th className="p-2 text-left">Açıklama</th>
                <th className="p-2 text-left">Kategori</th>
                <th className="p-2 text-right">Tutar</th>
                <th className="p-2 text-center">Durum</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {(detail.extra_debts || []).map(e => (
                <tr key={e.id} className="border-t">
                  <td className="p-2 text-slate-500">{(e.created_at || '').slice(0, 10)}</td>
                  <td className="p-2 font-medium">{e.description}</td>
                  <td className="p-2 text-slate-500">{e.category}</td>
                  <td className="p-2 text-right font-medium">{(e.amount || 0).toLocaleString('tr-TR')} TL</td>
                  <td className="p-2 text-center">
                    {e.is_paid
                      ? <span className="text-emerald-600 font-semibold">✓ Ödendi</span>
                      : <span className="text-red-600 font-semibold">✗ Ödenmedi</span>}
                  </td>
                  <td className="p-2">
                    <button onClick={() => onDeleteDebt(e.id)} className="text-red-500 hover:text-red-700">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
              {(!detail.extra_debts || detail.extra_debts.length === 0) && (
                <tr><td colSpan="6" className="p-6 text-center text-slate-400">Ek borç yok</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'makbuz' && (
        <div className="space-y-2">
          {(detail.receipts || []).map(r => (
            <div key={r.id} className="border rounded-lg p-3 bg-white">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded">{r.id.slice(0, 8).toUpperCase()}</span>
                  <span className="text-xs text-slate-500">{(r.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                </div>
                <div className="text-lg font-bold text-emerald-600">{r.total_amount.toLocaleString('tr-TR')} TL</div>
              </div>
              <div className="flex flex-wrap gap-1.5 text-xs">
                {(r.distributions || []).map((d, i) => (
                  <span key={i} className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                    {TYPE_LABELS[d.type] || d.type}: {d.amount.toLocaleString('tr-TR')} TL
                    {d.month && ` (${d.month})`}
                  </span>
                ))}
              </div>
            </div>
          ))}
          {(!detail.receipts || detail.receipts.length === 0) && (
            <div className="text-center py-8 text-slate-400 text-sm">Makbuz yok</div>
          )}
        </div>
      )}
    </div>
  );
}


// ==================== EVRAK / BELGE BÖLÜMÜ ====================
function EvrakSection({ apartmentId, headers, token }) {
  const API = `${RUNTIME_BACKEND_URL}/api/hesap`;
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({ baslik: '', kategori: 'fatura', not: '' });
  const [showUpload, setShowUpload] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/apartments/${apartmentId}/evraklar?year=${year}`, { headers });
      if (res.ok) setDocs(await res.json());
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apartmentId, year]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const onFileSelected = (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Dosya 10MB\'dan büyük olamaz');
      return;
    }
    setPendingFile(file);
    setUploadForm({ baslik: file.name.replace(/\.[^.]+$/, ''), kategori: 'fatura', not: '' });
    setShowUpload(true);
  };

  const submitUpload = async () => {
    if (!pendingFile) return;
    if (!uploadForm.baslik.trim()) { toast.error('Başlık gerekli'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', pendingFile);
      fd.append('baslik', uploadForm.baslik || '');
      fd.append('kategori', uploadForm.kategori || 'diger');
      fd.append('not', uploadForm.not || '');
      fd.append('year', String(year));
      const res = await fetch(`${API}/apartments/${apartmentId}/evraklar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      if (!res.ok) {
        let detail = `Hata kodu: ${res.status}`;
        try {
          const d = await res.json();
          detail = d.detail || detail;
        } catch {
          try { detail = await res.text(); } catch {}
        }
        throw new Error(detail);
      }
      toast.success('Evrak yüklendi');
      setShowUpload(false);
      setPendingFile(null);
      loadDocs();
    } catch (err) {
      toast.error(err.message || 'Yüklenemedi');
    } finally {
      setUploading(false);
    }
  };

  const deleteDoc = async (id) => {
    if (!window.confirm('Bu evrak silinsin mi?')) return;
    const res = await fetch(`${API}/apartments/${apartmentId}/evraklar/${id}`, { method: 'DELETE', headers });
    if (res.ok) { toast.success('Silindi'); loadDocs(); }
  };

  const KAT_LABELS = {
    fatura: 'Fatura', dekont: 'Dekont', makbuz: 'Makbuz',
    sozlesme: 'Sözleşme', tutanak: 'Tutanak', diger: 'Diğer'
  };
  const KAT_COLORS = {
    fatura: 'bg-blue-100 text-blue-700 border-blue-200',
    dekont: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    makbuz: 'bg-amber-100 text-amber-700 border-amber-200',
    sozlesme: 'bg-purple-100 text-purple-700 border-purple-200',
    tutanak: 'bg-pink-100 text-pink-700 border-pink-200',
    diger: 'bg-slate-100 text-slate-700 border-slate-200',
  };

  const isImage = (filename) => /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(filename || '');

  return (
    <div className="bg-white rounded-2xl border-2 border-purple-100 shadow-sm overflow-hidden" data-testid="evrak-section">
      <div className="bg-gradient-to-r from-purple-500 to-indigo-500 px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Folder className="h-5 w-5 text-white" />
          <h3 className="font-bold text-white text-sm">Evraklar / Belgeler</h3>
        </div>
        <div className="flex items-center gap-2">
          <select value={year} onChange={e => setYear(parseInt(e.target.value))}
            className="bg-white/20 text-white text-xs rounded px-2 py-1 border border-white/30 outline-none">
            {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map(y => (
              <option key={y} value={y} className="text-slate-800">{y}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="p-4">
        <div className="flex flex-wrap gap-2 mb-4">
          <Button size="sm" onClick={() => fileRef.current?.click()} className="bg-purple-600 hover:bg-purple-700"
            data-testid="upload-evrak-btn">
            <Upload className="h-3.5 w-3.5 mr-1" /> Dosya Yükle
          </Button>
          <Button size="sm" onClick={() => cameraRef.current?.click()} className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="camera-evrak-btn">
            <Camera className="h-3.5 w-3.5 mr-1" /> Telefondan Çek
          </Button>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden"
            onChange={e => { onFileSelected(e.target.files[0]); e.target.value = ''; }} />
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
            onChange={e => { onFileSelected(e.target.files[0]); e.target.value = ''; }} />
          <span className="text-xs text-slate-400 self-center">·  {docs.length} evrak ({year})</span>
        </div>

        {loading ? (
          <div className="text-center py-8 text-slate-400 text-sm">Yükleniyor...</div>
        ) : docs.length === 0 ? (
          <div className="text-center py-12">
            <FileX className="h-10 w-10 text-slate-200 mx-auto mb-2" />
            <p className="text-sm text-slate-400">{year} yılında evrak yok</p>
            <p className="text-xs text-slate-300 mt-1">Yukarıdaki butonlardan ekleyebilirsiniz</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {docs.map(d => (
              <div key={d.id} className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-all"
                data-testid={`evrak-${d.id}`}>
                <div className="h-32 bg-slate-50 flex items-center justify-center cursor-pointer" onClick={() => setPreviewDoc(d)}>
                  {isImage(d.filename) ? (
                    <img src={`${API}/apartments/${apartmentId}/evraklar/file/${d.filename}?token=${token}`}
                      alt={d.baslik} className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center p-3">
                      <FileText className="h-10 w-10 text-slate-300 mx-auto mb-1" />
                      <p className="text-[10px] text-slate-500 break-all">{d.filename?.split('.').pop()?.toUpperCase()}</p>
                    </div>
                  )}
                </div>
                <div className="p-2.5">
                  <div className="flex items-center justify-between gap-1 mb-1">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${KAT_COLORS[d.kategori] || KAT_COLORS.diger}`}>
                      {KAT_LABELS[d.kategori] || d.kategori}
                    </span>
                    <span className="text-[10px] text-slate-400">{d.created_at?.slice(0, 10)}</span>
                  </div>
                  <p className="text-xs font-semibold text-slate-700 truncate" title={d.baslik}>{d.baslik}</p>
                  {d.not && <p className="text-[10px] text-slate-400 truncate mt-0.5" title={d.not}>{d.not}</p>}
                  <div className="flex items-center gap-1 mt-2 pt-2 border-t border-slate-100">
                    <button onClick={() => setPreviewDoc(d)} className="flex-1 text-[10px] text-slate-500 hover:text-purple-600 flex items-center justify-center gap-1 py-1">
                      <Eye className="h-3 w-3" /> Görüntüle
                    </button>
                    <a href={`${API}/apartments/${apartmentId}/evraklar/file/${d.filename}?token=${token}&download=1`}
                      download={d.filename}
                      className="flex-1 text-[10px] text-slate-500 hover:text-blue-600 flex items-center justify-center gap-1 py-1">
                      <Download className="h-3 w-3" /> İndir
                    </a>
                    <button onClick={() => deleteDoc(d.id)} className="text-[10px] text-slate-400 hover:text-red-600 px-2 py-1">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Form Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Evrak Bilgileri</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {pendingFile && (
              <div className="bg-slate-50 rounded-lg p-3 flex items-center gap-2">
                {pendingFile.type?.startsWith('image/') ? (
                  <ImageIcon className="h-5 w-5 text-purple-500" />
                ) : (
                  <FileText className="h-5 w-5 text-blue-500" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{pendingFile.name}</p>
                  <p className="text-xs text-slate-400">{(pendingFile.size / 1024).toFixed(0)} KB</p>
                </div>
              </div>
            )}
            <div className="space-y-1">
              <Label>Başlık *</Label>
              <Input value={uploadForm.baslik} onChange={e => setUploadForm({...uploadForm, baslik: e.target.value})}
                placeholder="Örn: Asansör bakım faturası" data-testid="evrak-baslik" />
            </div>
            <div className="space-y-1">
              <Label>Kategori</Label>
              <select value={uploadForm.kategori} onChange={e => setUploadForm({...uploadForm, kategori: e.target.value})}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="evrak-kategori">
                <option value="fatura">Fatura</option>
                <option value="dekont">Dekont</option>
                <option value="makbuz">Makbuz</option>
                <option value="sozlesme">Sözleşme</option>
                <option value="tutanak">Tutanak</option>
                <option value="diger">Diğer</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label>Not (opsiyonel)</Label>
              <Input value={uploadForm.not} onChange={e => setUploadForm({...uploadForm, not: e.target.value})}
                placeholder="Açıklama..." />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => { setShowUpload(false); setPendingFile(null); }} className="flex-1">İptal</Button>
              <Button onClick={submitUpload} disabled={uploading} className="flex-1 bg-purple-600 hover:bg-purple-700" data-testid="evrak-save-btn">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1" />Yükle</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{previewDoc?.baslik}</DialogTitle></DialogHeader>
          {previewDoc && (
            <div className="space-y-3">
              {isImage(previewDoc.filename) ? (
                <img src={`${API}/apartments/${apartmentId}/evraklar/file/${previewDoc.filename}?token=${token}`}
                  alt={previewDoc.baslik} className="w-full max-h-[600px] object-contain rounded-lg border" />
              ) : (
                <div className="bg-slate-50 rounded-lg p-12 text-center">
                  <FileText className="h-16 w-16 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500">{previewDoc.filename}</p>
                  <a href={`${API}/apartments/${apartmentId}/evraklar/file/${previewDoc.filename}?token=${token}`}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-3 text-blue-600 hover:underline text-sm">
                    <Eye className="h-3.5 w-3.5" /> Yeni sekmede aç
                  </a>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Kategori:</span> <b>{KAT_LABELS[previewDoc.kategori]}</b></div>
                <div><span className="text-slate-500">Tarih:</span> <b>{previewDoc.created_at?.slice(0, 10)}</b></div>
              </div>
              {previewDoc.not && <p className="text-sm bg-slate-50 rounded-lg p-3 text-slate-600">{previewDoc.not}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

